// OMNI Design System Button Recipe for Chakra UI v3
// Based on OMNI CSS button styles

import { defineRecipe } from '@chakra-ui/react';

export const buttonRecipe = defineRecipe({
  className: 'omni-button',
  base: {
    // Base button styles
    display: 'inline-flex',
    alignItems: 'center',
    justifyContent: 'center',
    position: 'relative',
    whiteSpace: 'nowrap',
    verticalAlign: 'middle',
    outline: 'transparent',
    lineHeight: 'normal',
    minWidth: '0',
    fontFamily: 'body',
    fontWeight: 'medium',
    transition: 'all 200ms ease',
    cursor: 'pointer',
    userSelect: 'none',
    borderRadius: 'base', // 8px (--radius)
    border: '1px solid',
    borderColor: 'transparent',
    textDecoration: 'none',

    // Focus styles
    _focus: {
      outline: '2px solid',
      outlineColor: 'brand.500',
      outlineOffset: '2px',
    },

    // Disabled styles
    _disabled: {
      opacity: 0.6,
      cursor: 'not-allowed',
      pointerEvents: 'none',
    },

    // Loading styles
    _loading: {
      opacity: 0.8,
      cursor: 'default',
      pointerEvents: 'none',
    },
  },

  variants: {
    // Visual variants
    variant: {
      // Primary button with electric blue gradient
      primary: {
        background: 'linear-gradient(135deg, brand.500 0%, secondary.500 100%)', // --gradient-primary
        color: 'white',
        borderColor: 'brand.500',
        boxShadow: 'button-dark', // --button-shadow-dark

        _hover: {
          background:
            'linear-gradient(135deg, brand.600 0%, secondary.600 100%)',
          boxShadow: 'button-light',
          transform: 'translateY(-1px)',
        },

        _active: {
          background:
            'linear-gradient(135deg, brand.700 0%, secondary.700 100%)',
          transform: 'translateY(0)',
          boxShadow: 'button-dark',
        },
      },

      // Secondary button with outline style
      secondary: {
        background: 'transparent',
        color: 'brand.500',
        borderColor: 'brand.500',
        boxShadow: 'none',

        _hover: {
          background: 'brand.50',
          borderColor: 'brand.600',
          color: 'brand.600',
          transform: 'translateY(-1px)',
        },

        _active: {
          background: 'brand.100',
          borderColor: 'brand.700',
          color: 'brand.700',
          transform: 'translateY(0)',
        },
      },

      // Success button with mint gradient
      success: {
        background: 'linear-gradient(138deg, green.500, #00e3ae 95%)', // --gradient-success
        color: 'white',
        borderColor: 'green.500',
        boxShadow: 'button-success', // --button-shadow-success

        _hover: {
          background: 'linear-gradient(138deg, green.600, #00d09e 95%)',
          transform: 'translateY(-1px)',
        },

        _active: {
          background: 'linear-gradient(138deg, green.700, #00bd8e 95%)',
          transform: 'translateY(0)',
        },
      },

      // Danger button with melon gradient
      danger: {
        background: 'linear-gradient(137deg, red.500 4%, #f56c8e)', // --gradient-error
        color: 'white',
        borderColor: 'red.500',
        boxShadow: 'button-error', // --button-shadow-error

        _hover: {
          background: 'linear-gradient(137deg, red.600 4%, #f45982)',
          transform: 'translateY(-1px)',
        },

        _active: {
          background: 'linear-gradient(137deg, red.700 4%, #f34676)',
          transform: 'translateY(0)',
        },
      },

      // Warning button with honeycomb gradient
      warning: {
        background: 'linear-gradient(134deg, yellow.500 4%, #f8d991 95%)', // --gradient-honeycomb
        color: 'gray.900',
        borderColor: 'yellow.500',
        boxShadow: 'button-alert', // --button-shadow-alert

        _hover: {
          background: 'linear-gradient(134deg, yellow.600 4%, #f6d085 95%)',
          transform: 'translateY(-1px)',
        },

        _active: {
          background: 'linear-gradient(134deg, yellow.700 4%, #f4c779 95%)',
          transform: 'translateY(0)',
        },
      },

      // Ghost button - minimal style
      ghost: {
        background: 'transparent',
        color: 'brand.500',
        borderColor: 'transparent',
        boxShadow: 'none',

        _hover: {
          background: 'brand.50',
          color: 'brand.600',
        },

        _active: {
          background: 'brand.100',
          color: 'brand.700',
        },
      },

      // Link button - looks like a link
      link: {
        background: 'none',
        color: 'brand.500',
        borderColor: 'transparent',
        boxShadow: 'none',
        textDecoration: 'underline',
        minWidth: 'auto',
        height: 'auto',
        padding: '0',

        _hover: {
          color: 'brand.600',
          textDecoration: 'none',
        },

        _active: {
          color: 'brand.700',
        },
      },

      // Subtle button - very minimal
      subtle: {
        background: 'gray.100',
        color: 'gray.700',
        borderColor: 'gray.200',
        boxShadow: 'none',

        _hover: {
          background: 'gray.200',
          borderColor: 'gray.300',
        },

        _active: {
          background: 'gray.300',
          borderColor: 'gray.400',
        },
      },
    },

    // Size variants
    size: {
      xs: {
        fontSize: '2xs', // 11px (--size-7)
        height: '6', // 24px
        minWidth: '6', // 24px
        paddingX: '2', // 8px
        paddingY: '1', // 4px
      },

      sm: {
        fontSize: 'xs', // 12px (--size-6)
        height: '8', // 32px
        minWidth: '8', // 32px
        paddingX: '3', // 12px
        paddingY: '2', // 8px
      },

      md: {
        fontSize: 'md', // 14px (--size-4)
        height: '10', // 40px
        minWidth: '10', // 40px
        paddingX: '4', // 16px
        paddingY: '2.5', // 10px
      },

      lg: {
        fontSize: 'lg', // 16px (--size-3)
        height: '12', // 48px
        minWidth: '12', // 48px
        paddingX: '6', // 24px
        paddingY: '3', // 12px
      },

      xl: {
        fontSize: 'xl', // 18px (--size-2)
        height: '14', // 56px
        minWidth: '14', // 56px
        paddingX: '8', // 32px
        paddingY: '4', // 16px
      },
    },

    // Loading state
    loading: {
      true: {
        _before: {
          content: '""',
          position: 'absolute',
          width: '1em',
          height: '1em',
          border: '2px solid transparent',
          borderTop: '2px solid',
          borderRadius: 'full',
          animation: 'spin 1s linear infinite',
        },

        // Hide text when loading
        '& > *': {
          opacity: 0,
        },
      },
    },

    // Full width
    fullWidth: {
      true: {
        width: '100%',
      },
    },
  },

  compoundVariants: [
    // Primary button when loading
    {
      variant: 'primary',
      loading: true,
      css: {
        _before: {
          borderTopColor: 'white',
        },
      },
    },

    // Secondary button when loading
    {
      variant: 'secondary',
      loading: true,
      css: {
        _before: {
          borderTopColor: 'brand.500',
        },
      },
    },

    // Success button when loading
    {
      variant: 'success',
      loading: true,
      css: {
        _before: {
          borderTopColor: 'white',
        },
      },
    },

    // Danger button when loading
    {
      variant: 'danger',
      loading: true,
      css: {
        _before: {
          borderTopColor: 'white',
        },
      },
    },

    // Warning button when loading
    {
      variant: 'warning',
      loading: true,
      css: {
        _before: {
          borderTopColor: 'gray.900',
        },
      },
    },
  ],

  defaultVariants: {
    variant: 'primary',
    size: 'md',
    loading: false,
    fullWidth: false,
  },
});
